import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ConfigProvider, App as AntApp, Spin } from 'antd';
import type { Locale } from 'antd/es/locale';
import enUS from 'antd/locale/en_US';
import ruRU from 'antd/locale/ru_RU';
import ukUA from 'antd/locale/uk_UA';
import { useTranslation } from 'react-i18next';
import { themeConfig } from './theme/themeConfig';
import { useAuthStore } from './stores/authStore';
import LoginPage from './components/LoginPage';
import DashboardView from './components/DashboardView';
import EmbedView from './components/EmbedView';
import SharedView from './components/SharedView';
import VersionUpdateNotice from './components/VersionUpdateNotice';

// Map react-i18next language code → antd locale bundle.
// Embedded antd texts (Modal OK/Cancel, Empty "No data", pagination, form
// validation messages) follow this locale, so changes in i18next switch them too.
const ANTD_LOCALES: Record<string, Locale> = {
  en: enUS,
  ru: ruRU,
  uk: ukUA,
};

export function resolveAntdLocale(lang: string | undefined): Locale {
  // i18n.language может быть 'en-US' и т.п. — берём только базовый код.
  const base = (lang || 'ru').toLowerCase().split('-')[0];
  return ANTD_LOCALES[base] || enUS;
}

const DashboardRoute: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuthStore();

  useEffect(() => {
    useAuthStore.getState().fetchMe();
  }, []);

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!isAuthenticated) return <LoginPage />;
  return <DashboardView />;
};

const basePath = (import.meta.env.VITE_BASE_PATH || '/').replace(/\/+$/, '') || undefined;

// In Hub embed mode antd Tooltip/Popover/Modal portal into document.body by
// default; their hover-driven mount can cause the body to briefly grow,
// triggering iframe auto-resize and visible jitter (especially near right
// edge where tooltip overflow may add a scrollbar). Pin all popups inside
// #root so portal mounts don't affect document layout.
const popupContainer = (): HTMLElement => document.getElementById('root') ?? document.body;

const App: React.FC = () => {
  const { i18n } = useTranslation();
  const antdLocale = resolveAntdLocale(i18n.language);

  return (
    <ConfigProvider theme={themeConfig} locale={antdLocale} getPopupContainer={popupContainer}>
      <AntApp>
        <VersionUpdateNotice />
        <BrowserRouter
          basename={basePath}
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <Routes>
            <Route path="/" element={<DashboardRoute />} />
            <Route path="/embed" element={<EmbedView />} />
            <Route path="/hub" element={<EmbedView />} />
            <Route path="/s/:shareId" element={<SharedView />} />
          </Routes>
        </BrowserRouter>
      </AntApp>
    </ConfigProvider>
  );
};

export default App;
